import { formatAmount } from "../../../../utils/hooks/dataHandler";
import {
  autoTextColor,
  beautyAutoLine,
  boldAutoColumn,
} from "../../../../utils/pdf/page";
import {
  buildAutoTable,
  enhanceReferenceColumn,
  formatReferenceDetails,
} from "../../../../utils/pdf/buildAutoTable";
import { createMainComputationSgdColumn } from "../sgdColumn";
import {
  buildIrasSectionSpacer,
  getIrasTableCellPadding,
  getIrasTableStyles,
  isIrasSpacerRow,
  normaliseIrasAutoTextColumnSpacing,
} from "./tableSpacing";
import {
  getIrasMainCompColumnStyles,
  IRAS_TABLE_START_Y,
} from "./layoutConfig";
import {
  calculateIrasSgdSectionResults,
  getRenderableIrasMainCompSections,
  isIrasChargeableIncomeSection,
} from "./sgdCalculation";

const getLineLabel = (section) => section.label;

const buildHeaders = ({
  addSgdHeader,
  currencyView,
  forView,
  hasSgdColumn,
}) => {
  const headers = [
    "",
    boldAutoColumn(currencyView),
    boldAutoColumn(currencyView),
    boldAutoColumn("Sch"),
  ];

  if (hasSgdColumn) addSgdHeader(headers);

  if (forView) {
    headers.push({
      content: "Reference",
      styles: { textColor: [255, 0, 0], fontStyle: "bold" },
    });
  }

  return [headers];
};

export const renderIrasMainCompSections = ({
  company,
  currencyRate,
  currencyView,
  forView,
  isCompact = false,
  pdf,
  reviewNote = "",
  schedule,
  sections,
  tableSpacingOptions,
  unutilisedCapitalAllowances = [],
  unutilisedDonations = [],
  unutilisedLosses = [],
}) => {
  const body = [];
  const rowExchangeRates = new WeakMap();
  const sgdAmountOverrides = new WeakMap();
  const totalLines = [];
  const totalSubLines = [];
  const totalDoubleLines = [];
  const hasSgdColumn =
    String(currencyView ?? company?.currency ?? "SGD").toUpperCase() !== "SGD";
  const columnCount = 4 + (hasSgdColumn ? 1 : 0) + (forView ? 1 : 0);
  const spacingOptions = tableSpacingOptions ?? isCompact;
  const buildReferenceCell = (reference) =>
    autoTextColor(
      reference,
      [255, 0, 0],
      true,
      getIrasTableCellPadding(spacingOptions)
    );
  const { addSgdColumnThroughRow, addSgdHeader } =
    createMainComputationSgdColumn({
      currencyRate: currencyRate ?? company?.currencyRate ?? 1,
      rowExchangeRates,
      sgdAmountOverrides,
      shouldSkipRow: isIrasSpacerRow,
      unutilisedCapitalAllowances,
      unutilisedDonations,
      unutilisedLosses,
    });
  const renderedSections = getRenderableIrasMainCompSections(sections);
  const lastResultSectionIndex = renderedSections.reduce(
    (lastIndex, section, index) =>
      section.type === "result" ? index : lastIndex,
    -1
  );
  let chargeableIncomeBeforeExemptAmount = null;
  let resultAmounts = new WeakMap();
  let rowAmounts = new WeakMap();
  let sectionAmounts = new WeakMap();

  if (hasSgdColumn) {
    ({
      chargeableIncomeBeforeExemptAmount,
      resultAmounts,
      rowAmounts,
      sectionAmounts,
    } =
      calculateIrasSgdSectionResults({
        currencyRate: currencyRate ?? company?.currencyRate ?? 1,
        sections: renderedSections,
        unutilisedCapitalAllowances,
        unutilisedDonations,
        unutilisedLosses,
      }));
  } else {
    const chargeableIncomeSection = renderedSections.find(
      isIrasChargeableIncomeSection
    );
    chargeableIncomeBeforeExemptAmount = Math.max(
      0,
      Math.round(parseFloat(chargeableIncomeSection?.amount) || 0)
    );
  }

  renderedSections.forEach((section, sectionIndex) => {
    if (
      sectionIndex > 0 &&
      sectionIndex !== lastResultSectionIndex &&
      section.id !== "net_profit_before_tax" &&
      section.type !== "result"
    )
      body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    if (section.type === "result") {
      if (!section.suppressTopLine) {
        totalSubLines.push(body.length - 1);
      }
      const resultRow = [
        boldAutoColumn(getLineLabel(section)),
        "",
        formatAmount(section.amount),
      ];

      if (hasSgdColumn && resultAmounts.has(section)) {
        sgdAmountOverrides.set(resultRow, resultAmounts.get(section));
      }
      body.push(resultRow);

      const previousSection = renderedSections[sectionIndex - 1];
      if (
        previousSection?.suppressSubtotalLine &&
        !previousSection?.omitSubtotalRow
      ) {
        const previousRowIndex = body.length - 2;
        if (
          previousRowIndex >= 0 &&
          !totalSubLines.includes(previousRowIndex)
        ) {
          totalSubLines.push(previousRowIndex);
        }
      }

      if (sectionIndex === lastResultSectionIndex && hasSgdColumn) {
        totalSubLines.push(body.length - 1);
        totalDoubleLines.push(body.length - 1);
      } else if (section.doubleLine) {
        totalSubLines.push(body.length - 1);
        totalDoubleLines.push(body.length - 1);
      } else if (section.singleLine) {
        totalSubLines.push(body.length - 1);
      }

      return;
    }

    const hasSingleRow = section.rows.length === 1 && !section.forceMultiRow;

    if (hasSingleRow) {
      const [row] = section.rows;
      body.push([boldAutoColumn(getLineLabel(section))]);

      const content = [
        row.label,
        "",
        formatAmount(row.amount),
        row.schedule && `${row.schedule}`,
      ];
      rowExchangeRates.set(content, row.exchangeRate);
      if (hasSgdColumn && rowAmounts.has(row)) {
        sgdAmountOverrides.set(content, rowAmounts.get(row));
      }

      if (forView) content.push(buildReferenceCell(row.reference));

      body.push(content);
      // if (!section.suppressSubtotalLine) {
      //   totalSubLines.push(body.length - 1);
      // }
      return;
    }

    body.push([boldAutoColumn(getLineLabel(section))]);

    section.rows.forEach((row) => {
      const content = [
        row.label,
        formatAmount(row.amount),
        "",
        row.schedule && `${row.schedule}`,
      ];
      rowExchangeRates.set(content, row.exchangeRate);
      if (hasSgdColumn && rowAmounts.has(row)) {
        sgdAmountOverrides.set(content, rowAmounts.get(row));
      }

      if (forView) content.push(buildReferenceCell(row.reference));

      body.push(content);
    });

    if (section.rows.length > 0) {
      totalLines.push(body.length - 1);
    }

    const subtotalRow = ["", "", formatAmount(section.total)];
    if (hasSgdColumn && sectionAmounts.has(section)) {
      sgdAmountOverrides.set(subtotalRow, sectionAmounts.get(section));
    }

    if (section.omitSubtotalRow) return;

    body.push(subtotalRow);
    // if (!section.suppressSubtotalLine) {
    //   totalSubLines.push(body.length - 1);
    // }
  });

  if (hasSgdColumn) {
    addSgdColumnThroughRow(body, body.length - 1);
  }

  if (forView && body.length > 0) {
    const referenceColumnIndex = hasSgdColumn ? 5 : 4;
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      body,
      referenceColumnIndex,
      15
    );
    body.length = 0;
    body.push(...enhancedBody);
    normaliseIrasAutoTextColumnSpacing(
      body,
      referenceColumnIndex,
      spacingOptions
    );

    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNote !== "") reviewNote += "\n\n";
      reviewNote += "[Reference Details]\n" + refDetailsText;
    }
  }

  if (body.length > 0) {
    const detailLineColumns = [1];
    const totalLineColumns = [2];
    const columnStyles = getIrasMainCompColumnStyles(hasSgdColumn, forView);

    buildAutoTable({
      head: buildHeaders({
        addSgdHeader,
        currencyView,
        forView,
        hasSgdColumn,
      }),
      body,
      pdf,
      customStartY: IRAS_TABLE_START_Y,
      schedule,
      company,
      styles: getIrasTableStyles(pdf, spacingOptions),
      columnStyles,
      didDrawCell: function (data) {
        beautyAutoLine(
          data,
          pdf.doc,
          (totalLines.includes(data.row.index) &&
            detailLineColumns.includes(data.column.index)) ||
            (totalSubLines.includes(data.row.index) &&
              totalLineColumns.includes(data.column.index)),
          totalDoubleLines.includes(data.row.index) &&
            totalLineColumns.includes(data.column.index)
        );
      },
      lineSettings: {
        totalLines: totalSubLines,
        totalDoubleLines,
        subTotalLines: totalLines,
        totalRows: totalLineColumns,
        subTotalRows: detailLineColumns,
      },
    });
  }

  const getDeductionBalance = (id) => {
    const section = renderedSections.find((item) => item.id === id);
    return Math.max(0, -(sectionAmounts.get(section) ?? 0));
  };
  const getCarryForwardBalance = (label) => {
    const section = renderedSections.find(
      (item) => item.type === "result" && item.label === label
    );
    return Math.max(0, -(resultAmounts.get(section) ?? 0));
  };
  const unabsorbedSgdSummary = hasSgdColumn
    ? {
        caRows: {
          balanceAvailable: getDeductionBalance("unutilised_ca_bf") +
            getDeductionBalance("current_year_ca"),
          balanceCf: getCarryForwardBalance("Unutilised CA c/f"),
        },
        lossRows: {
          balanceAvailable: getDeductionBalance("unutilised_losses_bf"),
          balanceCf: getCarryForwardBalance("Unutilised Losses c/f"),
        },
        donationRows: {
          balanceAvailable: getDeductionBalance("unutilised_donations_bf") +
            getDeductionBalance("current_year_donations"),
          balanceCf: getCarryForwardBalance("Unutilised Donations c/f"),
        },
      }
    : undefined;

  return {
    bodyRowCount: body.length,
    chargeableIncomeBeforeExemptAmount,
    unabsorbedSgdSummary,
    reviewNote,
  };
};
